import { useEffect } from "react";
import { useOperatorAuthStore } from "src/store/operatorAuthStore";
import { getJwtExpiryMs, isJwtExpired } from "src/lib/jwt";
import { APP_ENV } from "src/lib/appEnv";

/**
 * The operator (iv3api) session authenticates against THIS build's environment
 * (see appEnv). Each site is a per-env deployment, so this is fixed at build
 * time; the OTP is locked to the signed-in Google account.
 */
export const OPERATOR_AUTH_ENV = APP_ENV;

/** Refresh this many ms before the token's `exp` so we never send a dead one. */
const REFRESH_SKEW_MS = 2 * 60_000;
/** Longest single setTimeout we schedule; re-armed by the token-change effect. */
const MAX_TIMER_MS = 10 * 60_000;

/**
 * Keeps the persisted operator token alive in the background:
 *   - On mount / token change, refreshes immediately if within the skew window.
 *   - Otherwise schedules a refresh shortly before expiry.
 * A failed refresh clears the token (handled in the store), which re-shows the
 * OTP gate. Safe to mount once, high in the tree, behind the Google session.
 */
export function useOperatorSession(enabled: boolean): void {
  const token = useOperatorAuthStore((s) => s.token);
  const refresh = useOperatorAuthStore((s) => s.refresh);

  useEffect(() => {
    if (!enabled || !token.trim()) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = () => {
      if (cancelled) return;
      const current = useOperatorAuthStore.getState().token;
      if (!current.trim()) return;
      const expMs = getJwtExpiryMs(current);
      // No readable expiry → we can't schedule proactively; the client's 401
      // interceptor is the refresh trigger for these tokens. Do nothing.
      if (expMs == null) return;
      if (isJwtExpired(current, REFRESH_SKEW_MS)) {
        void refresh();
        return;
      }
      const delay = Math.min(
        Math.max(expMs - Date.now() - REFRESH_SKEW_MS, 1_000),
        MAX_TIMER_MS,
      );
      timer = setTimeout(tick, delay);
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, token, refresh]);
}
